import styled from "@emotion/styled";
import {
  APP_BREAKPOINT,
  APP_CONTENT_WIDTH_WITHOUT_PADDING
} from "src/constants/layout";
import { CloseButton } from "src/ui/components/CareTeamSection/CareTeamRoleDetailSheet.styled";

type SpacerSize = "md" | "lg" | "xl";

/** Partner themes (e.g. Transcarent) still win via `--bg-primary-gradient`. */
const Screen = styled.div`
  label: healthInformationExchangeStep_screen;
  min-height: 100%;
  background: var(
    --bg-primary-gradient,
    linear-gradient(180deg, rgb(255 255 255 / 0.4) 25%, transparent 100%),
    radial-gradient(85% 40% at 0% 100%, #a4b9f4, transparent),
    linear-gradient(260deg, #fdcdab 25%, #ecc1e4 70%, #dec6ee 100%)
  );
  background-attachment: fixed;
  background-size: cover;
`;

const Content = styled.div`
  label: healthInformationExchangeStep_content;
  width: 100%;
  padding: 0.75rem 1.6rem 1.5rem;
  box-sizing: border-box;
  background: linear-gradient(
    180deg,
    var(--color-cream) 0%,
    var(--color-cream-transparent) 35%
  );
`;

const ContentInner = styled.div`
  label: healthInformationExchangeStep_contentInner;
  width: 100%;
  max-width: ${APP_CONTENT_WIDTH_WITHOUT_PADDING}px;
  margin: 0 auto;
  text-align: center;
`;

/**
 * Vertical gap matching the design tokens the old <nine-spacer s="…"> used.
 * `$size` maps straight onto the global `--space-*` scale.
 */
const Spacer = styled.div<{ $size: SpacerSize }>`
  label: healthInformationExchangeStep_spacer;
  height: var(--space-${({ $size }) => $size});
`;

const Hero = styled.img`
  label: healthInformationExchangeStep_hero;
  display: block;
  width: 230px;
  height: auto;
  /* The PNG carries transparent padding around the clipboard. */
  margin: -1.5rem auto -0.25rem;
`;

const Title = styled.h1`
  label: healthInformationExchangeStep_title;
  margin: 0;
  text-wrap: balance;

  /* Desktop h3 is larger; borrow the side padding so it fits on two lines. */
  @media screen and (min-width: ${APP_BREAKPOINT}px) {
    margin-inline: -1.6rem;
  }
`;

const Description = styled.p`
  label: healthInformationExchangeStep_description;
  margin: 0;
  font-weight: 400;
`;

const InfoCard = styled.div`
  label: healthInformationExchangeStep_infoCard;
  text-align: left;
  background:
    radial-gradient(
      circle at top left,
      rgba(255, 255, 255, 0.25),
      rgba(255, 255, 255, 0.1)
    ),
    linear-gradient(
      to right,
      rgba(255, 255, 255, 0.06),
      rgba(255, 255, 255, 0.17)
    );
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.45);
  border-radius: 2.25rem;
  box-shadow:
    0 5px 20px rgba(0, 0, 0, 0.08),
    inset 1px 1px 1px rgba(255, 255, 255, 0.9),
    inset -1px -1px 1px rgba(255, 255, 255, 0.5);
  overflow: hidden;
`;

const InfoRow = styled.div`
  label: healthInformationExchangeStep_infoRow;
  display: grid;
  grid-template-columns: 54px 1fr;
  gap: 0.75rem;
  align-items: start;
  padding: 1.25rem 1.25rem 1.25rem 0.75rem;

  & + & {
    border-top: 1px solid rgba(255, 255, 255, 0.7);
  }

  img {
    width: 54px;
    height: 54px;
  }

  h2 {
    margin: 0 0 0.25rem;
    font-weight: 500;
  }

  p {
    margin: 0;
    font-weight: 400;
  }
`;

const PrivacyNote = styled.div`
  label: healthInformationExchangeStep_privacyNote;
  display: flex;
  gap: 0.375rem;
  align-items: flex-start;
  padding: 0 0.75rem;
  text-align: left;
  color: var(--color-charcoal-80, rgba(33, 33, 33, 0.8));

  svg {
    flex-shrink: 0;
    width: 1rem;
    height: 1rem;
    margin-top: 0.125rem;
  }

  p {
    margin: 0;
  }
`;

const SheetTitle = styled.h2`
  label: healthInformationExchangeStep_sheetTitle;
  margin: -1.125rem 2.5rem 1.5rem 0;
  font-family: "STK Bureau", sans-serif;
  font-size: 1.25rem;
  font-weight: 500;
  line-height: 140%;
  letter-spacing: -0.02em;
`;

const SheetSection = styled.section`
  label: healthInformationExchangeStep_sheetSection;

  & + & {
    margin-top: 1.5rem;
  }

  h3 {
    display: flex;
    gap: 0.5rem;
    align-items: center;
    margin: 0 0 0.25rem;
    font-weight: 500;
  }

  svg {
    width: 1.125rem;
    height: 1.125rem;
    color: var(--color-gray, #aaa7a0);
  }

  p {
    margin: 0;
    font-weight: 400;
  }
`;

const SheetCloseButton = styled(CloseButton)`
  label: healthInformationExchangeStep_sheetCloseButton;
  top: 1.625rem;
  right: 1.25rem;
  background: rgba(33, 33, 33, 0.45);
  color: var(--color-white, #fff);

  &:hover {
    background: rgba(33, 33, 33, 0.55);
  }

  svg path {
    stroke: currentColor;
  }
`;

export const healthInformationExchangeStepStyled = {
  Screen,
  Content,
  ContentInner,
  Spacer,
  Hero,
  Title,
  Description,
  InfoCard,
  InfoRow,
  PrivacyNote,
  SheetTitle,
  SheetSection,
  SheetCloseButton
};
